import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import MailNotifyBridge, { ChimeToggle, SnoozeMenu } from '../components/mail/MailNotifyBridge';
import {
  Mail, Inbox, Send, Archive, Undo2, Search, RefreshCw, Loader2, Paperclip, X, Reply, ReplyAll,
  Forward, AlertTriangle, Download, ChevronLeft, ChevronDown, Plus, Star, Clock, Tag,
  FileText, CalendarClock, AlertOctagon, Keyboard, CheckSquare, Square, MailOpen, Bell, Trash2,
} from 'lucide-react';

/**
 * The Mailbox, v3 — conversations, schedule send, undo send, drafts, labels,
 * snooze, chimes. (mailbox-v3, 7 Sep 2026)
 * ==========================================================================
 *
 * Backed by:
 *   /api/inbox/threads*            routes/mailbox_threads.py   (conversations)
 *   /api/inbox/outbox*, /drafts*   routes/mailbox_outbox.py    (schedule/undo/drafts)
 *   /api/notifications/mail/poll   routes/mail_notify.py       (chime + toasts)
 *   /api/inbox/messages/{id}/*     routes/mailbox_inbox.py     (attachments, sync, poll)
 *
 * Three panes like Gmail: the rail (views, mailboxes, labels), the conversation
 * list, and the open conversation with EVERY message in it — inbound and our
 * replies — oldest at the top, the newest expanded, quoted history folded under
 * "…". Reply / Reply all / Forward sit inline at the bottom of the thread.
 * Below `lg` the panes stack: list, then the conversation with a back button.
 *
 * Rules carried over from the first Mailbox page, still in force:
 *  1. Nothing here is a permission check — the server decides what a caller sees.
 *  2. Archive is a flag; nothing is deleted, in Mongo or in Gmail.
 *  3. Message HTML renders in a sandboxed iframe without scripts.
 *  4. Polls are DB-only; the backend fetches mail on its own timer.
 */

// ---------------------------------------------------------------------------
// House style + constants
// ---------------------------------------------------------------------------

const C = {
  cream: '#faf7f0', card: '#ffffff', border: '#e5e1d5', ink: '#2b2b2b', muted: '#7a7a7a',
  gold: '#c9a84c', navy: '#2e3a68', soft: '#f4f1e8',
};
const FONT = "'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const VIEWS = [
  { id: 'inbox', label: 'Inbox', icon: Inbox },
  { id: 'starred', label: 'Starred', icon: Star },
  { id: 'snoozed', label: 'Snoozed', icon: Clock },
  { id: 'important', label: 'Important', icon: AlertOctagon },
  { id: 'sent', label: 'Sent', icon: Send },
  { id: 'scheduled', label: 'Scheduled', icon: CalendarClock },
  { id: 'drafts', label: 'Drafts', icon: FileText },
  { id: 'archived', label: 'Archived', icon: Archive },
];
const THREAD_VIEWS = new Set(['inbox', 'starred', 'snoozed', 'important', 'sent', 'archived', 'all', 'unread']);

const POLL_MS = 60000;
const PER_PAGE = 30;
const DRAFT_DEBOUNCE_MS = 1500;
const IST = 'Asia/Kolkata';

const SHORTCUTS = [
  ['c', 'Compose'], ['r', 'Reply'], ['a', 'Reply all'], ['f', 'Forward'], ['e', 'Archive'],
  ['j / k', 'Next / previous conversation'], ['Enter', 'Open the highlighted conversation'],
  ['u', 'Back to the list'], ['s', 'Star'], ['x', 'Select'], ['/', 'Search'],
  ['g i / g s', 'Go to Inbox / Sent'], ['?', 'This help'], ['Esc', 'Close'],
];

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((i) => (i && i.msg) || String(i)).join('; ');
  if (d) { try { return JSON.stringify(d); } catch (err) { return String(d); } }
  return e?.message || String(e);
};

const blobErrText = async (e) => {
  try {
    const data = e?.response?.data;
    if (data && typeof data.text === 'function') {
      const parsed = JSON.parse(await data.text());
      if (parsed?.detail) return parsed.detail;
    }
  } catch (ignored) { /* fall through */ }
  return errText(e);
};

const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });
};

const fmtDateLong = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const personLabel = (p) => (p?.name || '').trim() || p?.email || '';
const emailsOf = (list) => (list || []).map((p) => p?.email).filter(Boolean).join(', ');

const othersOn = (message, mailboxEmail) => {
  const own = (mailboxEmail || '').toLowerCase();
  const seen = new Set([own]);
  const out = [];
  [...(message?.to || []), ...(message?.cc || [])].forEach((p) => {
    const email = (p?.email || '').toLowerCase();
    if (email && !seen.has(email)) { seen.add(email); out.push(email); }
  });
  return out;
};

const quoteFor = (message) => {
  const who = personLabel({ name: message?.from_name, email: message?.from_email });
  const when = fmtDateLong(message?.date);
  const body = (message?.body_text || '').split('\n').map((l) => `> ${l}`).join('\n');
  return `\n\nOn ${when}, ${who} wrote:\n${body}\n`;
};

const forwardHeader = (m) => (
  `\n\n---------- Forwarded message ----------\nFrom: ${personLabel({ name: m.from_name, email: m.from_email })}`
  + `\nDate: ${fmtDateLong(m.date)}\nSubject: ${m.subject || ''}\nTo: ${emailsOf(m.to)}\n\n${m.body_text || ''}\n`
);

/** Split a plain-text body into what was written and what was quoted. */
const splitQuotedText = (text) => {
  const lines = (text || '').split('\n');
  let cut = -1;
  for (let i = 0; i < lines.length; i += 1) {
    const l = lines[i].trim();
    if (/^On .{6,}wrote:$/.test(l) || /^-{3,}\s*(Original|Forwarded) message\s*-{3,}$/i.test(l)
      || /^From: .+$/.test(l) && i + 1 < lines.length && /^(Sent|Date): /.test(lines[i + 1].trim())) { cut = i; break; }
    if (l.startsWith('>') && cut === -1) { cut = i; break; }
  }
  if (cut <= 0) return { main: text || '', quoted: '' };
  return { main: lines.slice(0, cut).join('\n').replace(/\s+$/, ''), quoted: lines.slice(cut).join('\n') };
};

/** Same for HTML: Gmail's gmail_quote div, or the first blockquote. */
const splitQuotedHtml = (html) => {
  if (!html) return { main: '', quoted: '' };
  const idx = [html.search(/<div[^>]+class="[^"]*gmail_quote/i), html.search(/<blockquote/i)]
    .filter((i) => i > 0);
  if (!idx.length) return { main: html, quoted: '' };
  const cut = Math.min(...idx);
  return { main: html.slice(0, cut), quoted: html.slice(cut) };
};

const ISTparts = () => {
  const now = new Date();
  const s = now.toLocaleString('en-US', { timeZone: IST, hour12: false, weekday: 'short', hour: '2-digit', minute: '2-digit' });
  const day = s.slice(0, 3);
  return { day };
};

// Gmail's four Send-later choices. Values are what the backend understands.
const schedulePresets = () => {
  const now = new Date();
  const at = (days, h, m) => {
    const d = new Date(now.getTime() + days * 86400000);
    // Label only; the server computes the real time in IST.
    return `${d.toLocaleDateString('en-IN', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short' })}, ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  const { day } = ISTparts();
  const daysToMonday = { Mon: 7, Tue: 6, Wed: 5, Thu: 4, Fri: 3, Sat: 2, Sun: 1 }[day] || 7;
  return [
    { id: 'tomorrow_morning', label: 'Tomorrow morning', sub: at(1, 8, 0) },
    { id: 'tomorrow_afternoon', label: 'Tomorrow afternoon', sub: at(1, 13, 0) },
    { id: 'monday_morning', label: 'Monday morning', sub: at(daysToMonday, 8, 0) },
  ];
};

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 1024 : false));
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

const isTyping = () => {
  const el = document.activeElement;
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
};

// ---------------------------------------------------------------------------
// Bodies
// ---------------------------------------------------------------------------

function HtmlBody({ html }) {
  const [height, setHeight] = useState(200);
  const srcDoc = useMemo(() => (
    '<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>'
    + 'html,body{margin:0;padding:0;background:#fff;color:#2b2b2b;font-family:Outfit,Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6;}'
    + 'body{overflow-wrap:break-word;word-break:break-word;}img{max-width:100%;height:auto;}table{max-width:100%;}'
    + 'a{color:#2e3a68;}blockquote{margin:0 0 0 12px;padding-left:12px;border-left:2px solid #e5e1d5;color:#7a7a7a;}'
    + '</style></head><body>' + (html || '') + '</body></html>'
  ), [html]);
  const fit = (event) => {
    try {
      const measured = event?.target?.contentDocument?.body?.scrollHeight;
      if (measured) setHeight(Math.min(Math.max(measured + 24, 60), 6000));
    } catch (ignored) { setHeight(600); }
  };
  return (
    <iframe
      title="Message body"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      onLoad={fit}
      style={{ width: '100%', border: 0, height, background: '#fff' }}
      data-testid="inbox-body-frame"
    />
  );
}

function TextBody({ text }) {
  return (
    <pre className="text-sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: FONT, color: C.ink, lineHeight: 1.65 }}>
      {text}
    </pre>
  );
}

/** A message body with its quoted history folded under "…". */
function FoldedBody({ message }) {
  const [showQuoted, setShowQuoted] = useState(false);
  const html = message.body_html || '';
  const text = message.body_text || '';
  const split = html ? splitQuotedHtml(html) : splitQuotedText(text);
  const hasQuoted = !!split.quoted;
  return (
    <div>
      {html ? <HtmlBody html={split.main} /> : <TextBody text={split.main} />}
      {hasQuoted && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowQuoted((v) => !v)}
            className="px-2 py-0.5 rounded-full text-xs"
            style={{ background: C.soft, color: C.muted, border: `1px solid ${C.border}` }}
            title={showQuoted ? 'Hide quoted text' : 'Show quoted text'}
            data-testid="quoted-toggle"
          >
            …
          </button>
          {showQuoted && (
            <div className="mt-2 pl-3" style={{ borderLeft: `2px solid ${C.border}` }}>
              {html ? <HtmlBody html={split.quoted} /> : <TextBody text={split.quoted} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AttachmentChips({ message }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const download = async (a) => {
    setBusy(a.part_id);
    setError('');
    try {
      const r = await api.get(`/inbox/messages/${message.id}/attachments/${a.part_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.filename || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(await blobErrText(e));
    } finally {
      setBusy('');
    }
  };
  const files = (message.attachments || []).filter((a) => !a.inline);
  if (!files.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="attachment-chips">
      {files.map((a) => (
        <button
          key={a.part_id}
          type="button"
          onClick={() => download(a)}
          className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full hover:opacity-80"
          style={{ background: C.cream, border: `1px solid ${C.border}`, color: C.ink }}
          title={`${a.filename} · ${fmtBytes(a.size)}`}
        >
          {busy === a.part_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" style={{ color: C.muted }} />}
          <span className="max-w-[200px] truncate">{a.filename}</span>
          <span style={{ color: C.muted }}>{fmtBytes(a.size)}</span>
        </button>
      ))}
      {error && <span className="text-xs text-red-700 self-center">{error}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One message inside a conversation
// ---------------------------------------------------------------------------

function MessageCard({ message, expanded, onToggle, onReply, onReplyAll, onForward, onStar, onRemind }) {
  const [remindMenu, setRemindMenu] = useState(false);
  const out = message.direction === 'outbound';
  const who = personLabel({ name: message.from_name, email: message.from_email });
  return (
    <div
      className="rounded-xl"
      style={{ background: out ? '#f6f8fd' : C.card, border: `1px solid ${C.border}` }}
      data-testid={`message-card-${message.id}`}
    >
      <button type="button" onClick={onToggle} className="w-full text-left px-4 py-3 flex items-start gap-3">
        <span
          className="mt-0.5 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
          style={{ background: out ? C.navy : C.gold, color: '#fff' }}
        >
          {(who || '?').slice(0, 1).toUpperCase()}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="text-sm font-semibold truncate" style={{ color: C.ink }}>{who}</span>
            {out && message.sent_by_email && <span className="text-[11px]" style={{ color: C.muted }}>sent by {message.sent_by_email}</span>}
            <span className="ml-auto text-xs flex-shrink-0" style={{ color: C.muted }} title={fmtDateLong(message.date)}>{fmtDateLong(message.date)}</span>
          </span>
          {expanded ? (
            <span className="block text-xs truncate" style={{ color: C.muted }}>
              to {emailsOf(message.to) || '—'}{message.cc?.length ? `, cc ${emailsOf(message.cc)}` : ''}
            </span>
          ) : (
            <span className="block text-xs truncate" style={{ color: C.muted }}>{message.snippet || '(no preview)'}</span>
          )}
        </span>
        {message.has_attachments && <Paperclip className="w-3.5 h-3.5 mt-1 flex-shrink-0" style={{ color: C.muted }} />}
        <Star
          className="w-4 h-4 mt-0.5 flex-shrink-0 cursor-pointer"
          style={{ color: message.starred ? C.gold : C.border, fill: message.starred ? C.gold : 'none' }}
          onClick={(e) => { e.stopPropagation(); onStar(message); }}
        />
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          {message.load_error && (
            <div className="mb-2 flex items-start gap-2 p-2 rounded-lg text-xs" style={{ background: '#fdf6e3', color: '#7a5a00' }}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {message.load_error}
            </div>
          )}
          <FoldedBody message={message} />
          <AttachmentChips message={message} />
          <div className="mt-3 flex flex-wrap gap-2 relative">
            {[['Reply', Reply, onReply], ['Reply all', ReplyAll, onReplyAll], ['Forward', Forward, onForward]].map(([label, Icon, fn]) => (
              <button
                key={label}
                type="button"
                onClick={() => fn(message)}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full hover:opacity-80"
                style={{ border: `1px solid ${C.border}`, color: C.ink, background: C.card }}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setRemindMenu((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full hover:opacity-80"
              style={{ border: `1px solid ${C.border}`, color: C.ink, background: C.card }}
              title="Remind me about this message"
            >
              <Bell className="w-3.5 h-3.5" /> Remind me
            </button>
            {remindMenu && (
              <div className="absolute left-0 top-9">
                <SnoozeMenu align="left" onPick={(c) => { setRemindMenu(false); onRemind(message, c); }} onClose={() => setRemindMenu(false)} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Send ▾ — Gmail's schedule menu
// ---------------------------------------------------------------------------

function ScheduleMenu({ onPick, onClose }) {
  const [custom, setCustom] = useState(false);
  const [when, setWhen] = useState('');
  const presets = useMemo(schedulePresets, []);
  return (
    <div
      className="absolute bottom-11 left-0 z-50 w-72 rounded-xl shadow-xl p-1 text-sm"
      style={{ background: C.card, border: `1px solid ${C.border}`, fontFamily: FONT }}
      data-testid="schedule-menu"
    >
      <div className="px-3 py-2 text-xs font-semibold" style={{ color: C.muted }}>Schedule send · India Standard Time</div>
      {!custom ? (
        <>
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick({ send_at: p.id, label: `${p.label} (${p.sub})` })}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 flex items-center justify-between"
              data-testid={`schedule-${p.id}`}
            >
              <span style={{ color: C.ink }}>{p.label}</span>
              <span className="text-xs" style={{ color: C.muted }}>{p.sub}</span>
            </button>
          ))}
          <button type="button" onClick={() => setCustom(true)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50" style={{ color: C.ink }}>
            Pick date &amp; time
          </button>
        </>
      ) : (
        <div className="p-3 space-y-2">
          <input
            type="datetime-local"
            value={when}
            min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)}
            onChange={(e) => setWhen(e.target.value)}
            className="w-full text-sm px-2 py-1.5 rounded-lg"
            style={{ border: `1px solid ${C.border}` }}
            data-testid="schedule-custom"
          />
          <p className="text-[11px]" style={{ color: C.muted }}>Read as India time. The message goes out from the server even if this tab is closed.</p>
          <div className="flex justify-end gap-2">
            <button type="button" className="text-xs" style={{ color: C.muted }} onClick={() => setCustom(false)}>Back</button>
            <button
              type="button"
              disabled={!when}
              onClick={() => onPick({ send_at: when, label: when.replace('T', ' ') + ' IST' })}
              className="text-xs px-3 py-1.5 rounded-lg text-white disabled:opacity-40"
              style={{ background: C.navy }}
            >
              Schedule send
            </button>
          </div>
        </div>
      )}
      <button type="button" className="w-full text-left px-3 py-1.5 text-xs" style={{ color: C.muted }} onClick={onClose}>Cancel</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The composer — new mail (modal) and reply / reply all / forward (inline)
// ---------------------------------------------------------------------------

function Composer({ mode, mailboxes, mailboxId, original, draft, inline, onClose, onQueued }) {
  const isReply = mode === 'reply' || mode === 'replyAll';
  const isForward = mode === 'forward';
  const sendable = mailboxes.filter((m) => m.can_send);
  const [from, setFrom] = useState(() => {
    if (draft?.mailbox_id) return draft.mailbox_id;
    const preferred = mailboxes.find((m) => m.id === mailboxId && m.can_send);
    return preferred?.id || sendable[0]?.id || mailboxId || mailboxes[0]?.id || '';
  });
  const activeMailbox = mailboxes.find((m) => m.id === (isReply || isForward ? (original?.mailbox_id || mailboxId) : from));

  const [to, setTo] = useState(() => {
    if (draft?.to) return Array.isArray(draft.to) ? draft.to.join(', ') : draft.to;
    if (isReply) return original?.reply_to || original?.from_email || '';
    return '';
  });
  const [cc, setCc] = useState(() => {
    if (draft?.cc) return Array.isArray(draft.cc) ? draft.cc.join(', ') : draft.cc;
    return mode === 'replyAll' ? othersOn(original, activeMailbox?.email).join(', ') : '';
  });
  const [bcc, setBcc] = useState(() => (draft?.bcc ? (Array.isArray(draft.bcc) ? draft.bcc.join(', ') : draft.bcc) : ''));
  const [showCcBcc, setShowCcBcc] = useState(() => !!(cc || bcc || mode === 'replyAll'));
  const [subject, setSubject] = useState(() => {
    if (draft?.subject) return draft.subject;
    if (!original) return '';
    const s = original.subject || '';
    if (isForward) return /^fwd?:/i.test(s) ? s : `Fwd: ${s}`;
    return /^re:/i.test(s) ? s : `Re: ${s}`;
  });
  const [body, setBody] = useState(() => {
    if (draft?.body_text) return draft.body_text;
    if (isForward && original) return forwardHeader(original);
    if (isReply && original) return quoteFor(original);
    return '';
  });
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [draftId, setDraftId] = useState(draft?.id || '');
  const [draftState, setDraftState] = useState('');
  const fileInput = useRef(null);
  const bodyRef = useRef(null);
  const dirty = useRef(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (isReply || isForward) {
      const el = bodyRef.current;
      if (el) { el.focus(); el.setSelectionRange(0, 0); }
    }
  }, [isReply, isForward]);

  // Drafts autosave — debounced, silent, never blocks typing.
  const persistDraft = useCallback(async () => {
    if (!dirty.current) return;
    dirty.current = false;
    try {
      const r = await api.post('/inbox/drafts', {
        id: draftId || undefined, mailbox_id: from, kind: mode === 'new' ? 'compose' : mode,
        reply_to_message_id: original?.id || null, thread_key: original?.thread_key || null,
        to, cc, bcc, subject, body_text: body, reply_all: mode === 'replyAll',
      });
      if (r.data?.draft?.id && !draftId) setDraftId(r.data.draft.id);
      setDraftState(`Draft saved ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`);
    } catch (e) {
      setDraftState('Draft not saved');
    }
  }, [draftId, from, mode, original, to, cc, bcc, subject, body]);

  useEffect(() => {
    if (!(to || subject || body.trim())) return undefined;
    dirty.current = true;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persistDraft, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(saveTimer.current);
  }, [to, cc, bcc, subject, body, from, persistDraft]);

  const addFiles = (incoming) => {
    const list = Array.from(incoming || []).filter(Boolean);
    if (list.length) setFiles((prev) => [...prev, ...list]);
  };
  const totalBytes = files.reduce((sum, f) => sum + (f.size || 0), 0);

  const submit = async (schedule) => {
    setError('');
    setScheduleOpen(false);
    if (!to.trim()) { setError(isForward ? 'Who should this be forwarded to?' : 'Add at least one recipient.'); return; }
    const form = new FormData();
    form.append('to', to);
    form.append('cc', cc);
    form.append('bcc', bcc);
    form.append('subject', subject);
    form.append('body_text', body);
    if (schedule?.send_at) form.append('send_at', schedule.send_at);
    if (draftId) form.append('draft_id', draftId);
    if (mode === 'new' || isForward) form.append('mailbox_id', isForward ? (original?.mailbox_id || from) : from);
    if (isReply) {
      form.append('reply_to_message_id', original.id);
      form.append('reply_all', mode === 'replyAll' ? 'true' : 'false');
    }
    files.forEach((f) => form.append('files', f, f.name));
    setSending(true);
    try {
      clearTimeout(saveTimer.current);
      dirty.current = false;
      // Content-Type undefined on purpose: lets the browser write the multipart boundary.
      const r = await api.post('/inbox/outbox', form, { headers: { 'Content-Type': undefined } });
      onQueued(r.data?.outbox, r.data?.undo_seconds ?? 0, schedule?.label, {
        mode, mailboxId: from, original, to, cc, bcc, subject, body,
      });
    } catch (e) {
      setError(errText(e));
    } finally {
      setSending(false);
    }
  };

  const discard = async () => {
    clearTimeout(saveTimer.current);
    if (draftId) api.delete(`/inbox/drafts/${draftId}`).catch(() => {});
    onClose();
  };

  const title = mode === 'new' ? 'New message' : mode === 'forward' ? 'Forward' : mode === 'replyAll' ? 'Reply to everyone' : 'Reply';

  const formBody = (
    <form
      onSubmit={(e) => { e.preventDefault(); submit(null); }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer?.files); }}
      className={`flex flex-col ${inline ? 'rounded-xl' : 'w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[92vh]'}`}
      style={{ background: C.card, border: `1px solid ${dragging ? C.gold : C.border}`, fontFamily: FONT }}
      data-testid="inbox-composer"
    >
      <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: `1px solid ${C.border}` }}>
        <h3 className="text-sm font-semibold" style={{ color: C.ink }}>{title}</h3>
        <div className="flex items-center gap-3">
          {draftState && <span className="text-[11px]" style={{ color: C.muted }}>{draftState}</span>}
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded-lg hover:opacity-70">
            <X className="w-4 h-4" style={{ color: C.muted }} />
          </button>
        </div>
      </div>

      <div className="px-4 py-3 space-y-2 overflow-y-auto">
        <div className="flex items-center gap-2 text-sm">
          <span className="w-12 text-xs" style={{ color: C.muted }}>From</span>
          {mode === 'new' ? (
            <select value={from} onChange={(e) => setFrom(e.target.value)} className="flex-1 text-sm px-2 py-1.5 rounded-lg bg-white" style={{ border: `1px solid ${C.border}` }} data-testid="composer-from">
              {sendable.map((m) => <option key={m.id} value={m.id}>{m.display_name} — {m.email}</option>)}
              {!sendable.length && <option value="">No mailbox can send yet — add an App Password in Settings › Mailboxes</option>}
            </select>
          ) : (
            <span className="flex-1 text-sm" style={{ color: C.ink }}>{activeMailbox?.display_name} — {activeMailbox?.email}</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-12 text-xs" style={{ color: C.muted }}>To</span>
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@company.com, other@company.com" className="flex-1 text-sm px-2 py-1.5 rounded-lg" style={{ border: `1px solid ${C.border}` }} data-testid="composer-to" autoFocus={mode === 'new' || isForward} />
          {!showCcBcc && <button type="button" className="text-xs" style={{ color: C.navy }} onClick={() => setShowCcBcc(true)}>Cc / Bcc</button>}
        </div>
        {showCcBcc && (
          <>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-12 text-xs" style={{ color: C.muted }}>Cc</span>
              <input value={cc} onChange={(e) => setCc(e.target.value)} className="flex-1 text-sm px-2 py-1.5 rounded-lg" style={{ border: `1px solid ${C.border}` }} data-testid="composer-cc" />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-12 text-xs" style={{ color: C.muted }}>Bcc</span>
              <input value={bcc} onChange={(e) => setBcc(e.target.value)} className="flex-1 text-sm px-2 py-1.5 rounded-lg" style={{ border: `1px solid ${C.border}` }} data-testid="composer-bcc" />
            </div>
          </>
        )}
        <div className="flex items-center gap-2 text-sm">
          <span className="w-12 text-xs" style={{ color: C.muted }}>Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="flex-1 text-sm px-2 py-1.5 rounded-lg" style={{ border: `1px solid ${C.border}` }} data-testid="composer-subject" />
        </div>
        <textarea
          ref={bodyRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={inline ? 8 : 12}
          placeholder="Write your message. Drag files anywhere on this box to attach them."
          className="w-full text-sm px-3 py-2 rounded-lg bg-white"
          style={{ border: `1px solid ${C.border}`, color: C.ink, fontFamily: FONT, resize: 'vertical' }}
          data-testid="composer-body"
        />
        {isForward && original?.has_attachments && (
          <p className="text-[11px] rounded-lg p-2" style={{ background: `${C.gold}18`, color: C.ink }}>
            The original attachments are not carried over. Download them from the message and attach them here if they should go with this forward.
          </p>
        )}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="composer-files">
            {files.map((f, i) => (
              <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full" style={{ background: C.cream, border: `1px solid ${C.border}` }}>
                <Paperclip className="w-3 h-3" style={{ color: C.muted }} />
                <span className="max-w-[180px] truncate">{f.name}</span>
                <span style={{ color: C.muted }}>{fmtBytes(f.size)}</span>
                <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>
                  <X className="w-3 h-3" style={{ color: C.muted }} />
                </button>
              </span>
            ))}
            <span className="text-[11px] self-center" style={{ color: C.muted }}>{fmtBytes(totalBytes)} total</span>
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 p-2.5 rounded-lg text-xs" style={{ background: '#fdf1f1', color: '#b42318' }} data-testid="composer-error">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" /><span className="break-words">{error}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 px-4 py-3 relative" style={{ borderTop: `1px solid ${C.border}` }}>
        <div className="inline-flex rounded-lg overflow-hidden" style={{ border: `1px solid ${C.navy}` }}>
          <button type="submit" disabled={sending} className="flex items-center gap-1.5 text-xs px-3.5 py-2 text-white disabled:opacity-40" style={{ background: C.navy }} data-testid="composer-send">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {sending ? 'Sending…' : 'Send'}
          </button>
          <button type="button" disabled={sending} onClick={() => setScheduleOpen((v) => !v)} className="px-2 text-white disabled:opacity-40" style={{ background: C.navy, borderLeft: '1px solid rgba(255,255,255,0.3)' }} aria-label="Schedule send" data-testid="composer-send-menu">
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>
        {scheduleOpen && <ScheduleMenu onPick={(s) => submit(s)} onClose={() => setScheduleOpen(false)} />}
        <button type="button" onClick={() => fileInput.current?.click()} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg" style={{ border: `1px solid ${C.border}`, color: C.ink }}>
          <Paperclip className="w-3.5 h-3.5" /> Attach
        </button>
        <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <span className="ml-auto" />
        <button type="button" onClick={discard} className="flex items-center gap-1 text-xs px-2 py-2 rounded-lg" style={{ color: C.muted }} title="Discard draft">
          <Trash2 className="w-3.5 h-3.5" /> Discard
        </button>
      </div>
    </form>
  );

  if (inline) return formBody;
  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center p-0 sm:p-6" style={{ background: 'rgba(43,43,43,0.45)' }}>
      {formBody}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Conversation view
// ---------------------------------------------------------------------------

function ThreadView({ data, mailboxes, onBack, onAction, onSnooze, onRemind, onQueued, composer, setComposer, notice }) {
  const { thread, messages } = data;
  const [expanded, setExpanded] = useState(() => new Set(messages.length ? [messages[messages.length - 1].id] : []));
  const [snoozeMenu, setSnoozeMenu] = useState(false);
  const [labelMenu, setLabelMenu] = useState(false);
  const [labels, setLabels] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    setExpanded(new Set(messages.length ? [messages[messages.length - 1].id] : []));
  }, [thread.thread_key, messages.length]);

  useEffect(() => {
    if (composer) bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [composer]);

  const loadLabels = async () => {
    try { const r = await api.get('/inbox/labels'); setLabels(r.data.labels || []); } catch (e) { /* ignore */ }
  };

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allOpen = expanded.size === messages.length;
  const latest = messages[messages.length - 1];
  const reply = (m, mode) => setComposer({ mode, original: m, mailboxId: m.mailbox_id });

  const actionBtn = (label, Icon, fn, extra = {}) => (
    <button
      type="button"
      onClick={fn}
      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg hover:opacity-80"
      style={{ border: `1px solid ${C.border}`, color: C.ink, background: C.card, ...extra.style }}
      title={extra.title || label}
      data-testid={extra.testid}
    >
      <Icon className="w-3.5 h-3.5" style={extra.iconStyle} /> <span className="hidden xl:inline">{label}</span>
    </button>
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 py-3 flex flex-wrap items-center gap-2 relative" style={{ borderBottom: `1px solid ${C.border}`, background: C.card }}>
        <button type="button" onClick={onBack} className="lg:hidden p-1 rounded-lg" aria-label="Back"><ChevronLeft className="w-5 h-5" style={{ color: C.muted }} /></button>
        {thread.archived
          ? actionBtn('Put back', Undo2, () => onAction('unarchive'), { testid: 'thread-unarchive' })
          : actionBtn('Archive', Archive, () => onAction('archive'), { testid: 'thread-archive' })}
        {actionBtn('Mark unread', MailOpen, () => onAction('unread'))}
        {actionBtn(thread.starred ? 'Unstar' : 'Star', Star, () => onAction(thread.starred ? 'unstar' : 'star'), { iconStyle: { color: thread.starred ? C.gold : undefined, fill: thread.starred ? C.gold : 'none' } })}
        {actionBtn(thread.important ? 'Not important' : 'Important', AlertOctagon, () => onAction(thread.important ? 'unimportant' : 'important'), { iconStyle: { color: thread.important ? '#b45309' : undefined } })}
        <div className="relative">
          {actionBtn(thread.snoozed_until ? 'Unsnooze' : 'Snooze', Clock, () => (thread.snoozed_until ? onAction('unsnooze') : setSnoozeMenu((v) => !v)), { testid: 'thread-snooze' })}
          {snoozeMenu && <SnoozeMenu align="left" onPick={(c) => { setSnoozeMenu(false); onSnooze(c); }} onClose={() => setSnoozeMenu(false)} />}
        </div>
        <div className="relative">
          {actionBtn('Labels', Tag, () => { setLabelMenu((v) => !v); if (!labelMenu) loadLabels(); })}
          {labelMenu && (
            <div className="absolute left-0 z-50 mt-1 w-56 rounded-lg shadow-lg p-1 text-sm" style={{ background: C.card, border: `1px solid ${C.border}` }}>
              {labels.length === 0 && <div className="px-2 py-1 text-xs" style={{ color: C.muted }}>No labels yet</div>}
              {labels.map((l) => {
                const on = (thread.labels || []).includes(l);
                return (
                  <button key={l} type="button" className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 flex items-center gap-2" onClick={() => onAction('labels', on ? { remove: [l] } : { add: [l] })}>
                    {on ? <CheckSquare className="w-3.5 h-3.5" style={{ color: C.navy }} /> : <Square className="w-3.5 h-3.5" style={{ color: C.muted }} />} {l}
                  </button>
                );
              })}
              <form className="flex gap-1 p-1" onSubmit={async (e) => {
                e.preventDefault();
                const name = newLabel.trim();
                if (!name) return;
                try { await api.post('/inbox/labels', { name }); } catch (err) { /* ignore */ }
                setNewLabel('');
                onAction('labels', { add: [name] });
                loadLabels();
              }}>
                <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="New label" className="flex-1 text-xs px-2 py-1 rounded" style={{ border: `1px solid ${C.border}` }} />
                <button type="submit" className="text-xs px-2 rounded text-white" style={{ background: C.navy }}>Add</button>
              </form>
            </div>
          )}
        </div>
        <span className="ml-auto text-xs" style={{ color: C.muted }}>
          {messages.length} message{messages.length === 1 ? '' : 's'} ·{' '}
          <button type="button" className="underline" onClick={() => setExpanded(allOpen ? new Set([latest?.id]) : new Set(messages.map((m) => m.id)))}>
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ background: C.cream }}>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold" style={{ color: C.ink, fontFamily: FONT }}>{thread.subject}</h2>
          {(thread.labels || []).map((l) => <span key={l} className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: C.soft, border: `1px solid ${C.border}`, color: C.ink }}>{l}</span>)}
          {thread.snoozed_until && <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: '#fff7e0', border: '1px solid #f0d58a', color: '#7a5a00' }}>Snoozed until {fmtDateLong(thread.snoozed_until)}</span>}
          {(thread.mailboxes || []).map((mb) => <span key={mb} className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: C.card, border: `1px solid ${C.border}`, color: C.muted }}>{mailboxes.find((m) => m.id === mb)?.email || mb}</span>)}
        </div>
        {notice && <div className="text-xs rounded-lg p-2" style={{ background: '#eef6ee', color: '#1e6b2f' }}>{notice}</div>}
        {messages.map((m) => (
          <MessageCard
            key={m.id}
            message={m}
            expanded={expanded.has(m.id)}
            onToggle={() => toggle(m.id)}
            onReply={(x) => reply(x, 'reply')}
            onReplyAll={(x) => reply(x, 'replyAll')}
            onForward={(x) => reply(x, 'forward')}
            onStar={(x) => onAction(x.starred ? 'unstar' : 'star', {}, x.id)}
            onRemind={onRemind}
          />
        ))}

        {composer ? (
          <Composer
            inline
            mode={composer.mode}
            mailboxes={mailboxes}
            mailboxId={composer.mailboxId}
            original={composer.original}
            draft={composer.draft}
            onClose={() => setComposer(null)}
            onQueued={onQueued}
          />
        ) : latest && (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="thread-reply-bar">
            {[['Reply', Reply, 'reply'], ['Reply all', ReplyAll, 'replyAll'], ['Forward', Forward, 'forward']].map(([label, Icon, mode]) => (
              <button key={mode} type="button" onClick={() => reply(latest, mode)} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full hover:opacity-80" style={{ border: `1px solid ${C.border}`, color: C.ink, background: C.card }}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scheduled + drafts lists
// ---------------------------------------------------------------------------

function ScheduledList({ items, onCancel, onSendNow, onReschedule, onOpen }) {
  if (!items.length) {
    return <div className="p-8 text-center text-sm" style={{ color: C.muted }}>Nothing scheduled. Use Send ▾ in the composer to pick a time.</div>;
  }
  return (
    <div className="divide-y" style={{ borderColor: C.border }}>
      {items.map((it) => (
        <div key={it.id} className="px-3 py-3 text-sm" data-testid={`scheduled-${it.id}`}>
          <div className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4" style={{ color: it.status === 'failed' ? '#b42318' : C.gold }} />
            <span className="font-medium truncate" style={{ color: C.ink }}>{it.subject}</span>
            <span className="ml-auto text-xs whitespace-nowrap" style={{ color: C.muted }}>{it.send_at_ist || fmtDateLong(it.send_at)}</span>
          </div>
          <div className="text-xs truncate" style={{ color: C.muted }}>to {(it.to || []).join(', ')} · from {it.mailbox_email}{it.user?.full_name ? ` · by ${it.user.full_name}` : ''}</div>
          {it.status === 'failed' && <div className="text-xs mt-1" style={{ color: '#b42318' }}>Not sent after {it.attempts} tries: {it.last_error}</div>}
          {it.status === 'scheduled' && it.attempts > 0 && <div className="text-xs mt-1" style={{ color: '#7a5a00' }}>Retrying (attempt {it.attempts} failed: {it.last_error})</div>}
          <div className="mt-1.5 flex flex-wrap gap-3 text-xs">
            <button type="button" className="underline" style={{ color: C.navy }} onClick={() => onOpen(it)}>Open</button>
            {it.status === 'scheduled' && <button type="button" className="underline" style={{ color: C.navy }} onClick={() => onSendNow(it)}>Send now</button>}
            {it.status === 'scheduled' && <button type="button" className="underline" style={{ color: C.navy }} onClick={() => onReschedule(it)}>Change time</button>}
            {it.status === 'scheduled' && <button type="button" className="underline" style={{ color: '#b42318' }} onClick={() => onCancel(it)}>Cancel</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function DraftsList({ items, onOpen, onDelete }) {
  if (!items.length) return <div className="p-8 text-center text-sm" style={{ color: C.muted }}>No drafts. Anything you start writing is saved here automatically.</div>;
  return (
    <div className="divide-y" style={{ borderColor: C.border }}>
      {items.map((d) => (
        <div key={d.id} className="px-3 py-3 text-sm flex items-start gap-2" data-testid={`draft-${d.id}`}>
          <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onOpen(d)}>
            <span className="block font-medium truncate" style={{ color: C.ink }}>{d.subject || '(no subject)'}</span>
            <span className="block text-xs truncate" style={{ color: C.muted }}>to {Array.isArray(d.to) ? d.to.join(', ') : (d.to || '—')} · {(d.body_text || '').slice(0, 80)}</span>
            <span className="block text-[11px]" style={{ color: C.muted }}>{fmtDateLong(d.updated_at)}</span>
          </button>
          <button type="button" aria-label="Delete draft" onClick={() => onDelete(d)}><Trash2 className="w-4 h-4" style={{ color: C.muted }} /></button>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toasts: undo send / notices, and the help overlay
// ---------------------------------------------------------------------------

function UndoToast({ item, onUndo, onDismiss }) {
  const [left, setLeft] = useState(() => Math.max(0, Math.round((item.expires - Date.now()) / 1000)));
  useEffect(() => {
    const t = setInterval(() => {
      const remaining = Math.max(0, Math.round((item.expires - Date.now()) / 1000));
      setLeft(remaining);
      if (remaining <= 0) { clearInterval(t); onDismiss(); }
    }, 500);
    return () => clearInterval(t);
  }, [item, onDismiss]);
  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[65] rounded-xl shadow-lg px-4 py-2.5 flex items-center gap-3 text-sm" style={{ background: C.ink, color: '#fff', fontFamily: FONT }} data-testid="undo-toast">
      <span>{item.label || 'Message sent'}</span>
      {item.undoable && <button type="button" onClick={onUndo} className="font-semibold underline" style={{ color: C.gold }} data-testid="undo-send">Undo ({left}s)</button>}
      {item.link && <button type="button" onClick={item.link.onClick} className="underline" style={{ color: C.gold }}>{item.link.label}</button>}
      <button type="button" aria-label="Dismiss" onClick={onDismiss}><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function HelpOverlay({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(43,43,43,0.45)' }} onClick={onClose}>
      <div className="rounded-2xl shadow-xl p-5 w-full max-w-md" style={{ background: C.card, fontFamily: FONT }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ color: C.ink }}>Keyboard shortcuts</h3>
          <button type="button" onClick={onClose}><X className="w-4 h-4" style={{ color: C.muted }} /></button>
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          {SHORTCUTS.map(([k, v]) => (
            <div key={k} className="contents">
              <kbd className="px-1.5 py-0.5 rounded text-xs font-mono" style={{ background: C.soft, border: `1px solid ${C.border}` }}>{k}</kbd>
              <span style={{ color: C.ink }}>{v}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs" style={{ color: C.muted }}>Search understands from:, to:, subject:, label:, has:attachment, is:unread, is:starred, is:important.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export default function InboxPageV3() {
  const [params, setParams] = useSearchParams();
  const mobile = useIsMobile();

  const [mailboxes, setMailboxes] = useState([]);
  const [unread, setUnread] = useState({});
  const [view, setView] = useState(() => params.get('view') || 'inbox');
  const [mailboxId, setMailboxId] = useState(() => params.get('mailbox') || '');
  const [label, setLabel] = useState('');
  const [labels, setLabels] = useState([]);
  const [queryText, setQueryText] = useState('');
  const [q, setQ] = useState('');
  const [threads, setThreads] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [cursor, setCursor] = useState(-1);
  const [openKey, setOpenKey] = useState(() => params.get('thread') || '');
  const [threadData, setThreadData] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [composer, setComposer] = useState(null);      // modal: { mode, mailboxId, original?, draft? }
  const [inlineComposer, setInlineComposer] = useState(null);
  const [undo, setUndo] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [helpOpen, setHelpOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [mobilePane, setMobilePane] = useState('list');
  const searchRef = useRef(null);
  const listToken = useRef(0);
  const goPrefix = useRef(false);

  // ---- loaders ----------------------------------------------------------

  const loadMailboxes = useCallback(async () => {
    try {
      const r = await api.get('/inbox/mailboxes');
      const rows = r.data?.mailboxes || [];
      setMailboxes(rows);
      setUnread(Object.fromEntries(rows.map((m) => [m.id, m.unread || 0])));
      setError('');
    } catch (e) { setError(errText(e)); }
  }, []);

  const loadLabels = useCallback(async () => {
    try { const r = await api.get('/inbox/labels'); setLabels(r.data.labels || []); } catch (e) { /* ignore */ }
  }, []);

  const loadThreads = useCallback(async (nextPage = 1, append = false) => {
    if (!THREAD_VIEWS.has(view) && view !== 'label') return;
    const token = ++listToken.current;
    setLoadingList(true);
    try {
      const r = await api.get('/inbox/threads', {
        params: { mailbox_id: mailboxId || undefined, view: view === 'label' ? 'all' : view, q: q || undefined, label: label || undefined, page: nextPage, per_page: PER_PAGE },
      });
      if (token !== listToken.current) return;
      const rows = r.data?.threads || [];
      setThreads((prev) => (append ? [...prev, ...rows] : rows));
      setHasMore(!!r.data?.has_more);
      setTotal(r.data?.total || 0);
      setPage(nextPage);
      setError('');
    } catch (e) {
      if (token === listToken.current) setError(errText(e));
    } finally {
      if (token === listToken.current) setLoadingList(false);
    }
  }, [view, mailboxId, q, label]);

  const loadOutbox = useCallback(async () => {
    try { const r = await api.get('/inbox/outbox', { params: { status: 'scheduled,failed' } }); setOutbox(r.data.items || []); } catch (e) { setError(errText(e)); }
  }, []);

  const loadDrafts = useCallback(async () => {
    try { const r = await api.get('/inbox/drafts'); setDrafts(r.data.drafts || []); } catch (e) { setError(errText(e)); }
  }, []);

  const openThread = useCallback(async (key, mailbox) => {
    if (!key) return;
    setOpenKey(key);
    setInlineComposer(null);
    setLoadingThread(true);
    setMobilePane('thread');
    try {
      const r = await api.get(`/inbox/threads/${encodeURIComponent(key)}`, { params: { mailbox_id: mailbox || mailboxId || undefined } });
      setThreadData(r.data);
      setThreads((prev) => prev.map((t) => (t.thread_key === key ? { ...t, unread: 0 } : t)));
      setError('');
    } catch (e) {
      setError(errText(e));
      setThreadData(null);
    } finally {
      setLoadingThread(false);
    }
  }, [mailboxId]);

  // ---- effects ----------------------------------------------------------

  useEffect(() => { loadMailboxes(); loadLabels(); }, [loadMailboxes, loadLabels]);
  useEffect(() => { loadThreads(1, false); setSelected(new Set()); setCursor(-1); }, [loadThreads]);
  useEffect(() => { if (view === 'scheduled') loadOutbox(); if (view === 'drafts') loadDrafts(); }, [view, loadOutbox, loadDrafts]);

  useEffect(() => {
    const t = setTimeout(() => setQ(queryText.trim()), 300);
    return () => clearTimeout(t);
  }, [queryText]);

  // URL <-> state (deep links from the bell / toasts: ?thread=&mailbox=)
  useEffect(() => {
    const key = params.get('thread');
    if (key && key !== openKey) openThread(key, params.get('mailbox') || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (view !== 'inbox') next.set('view', view);
    if (mailboxId) next.set('mailbox', mailboxId);
    if (openKey) next.set('thread', openKey);
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, mailboxId, openKey]);

  useEffect(() => {
    if (openKey && !threadData && !loadingThread) openThread(openKey, mailboxId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cheap poll: unread counts, and a list refresh on the Inbox.
  useEffect(() => {
    const tick = async () => {
      try {
        const r = await api.get('/inbox/poll');
        if (r.data?.unread_by_mailbox) setUnread(r.data.unread_by_mailbox);
        if (view === 'inbox' && page === 1 && !q) loadThreads(1, false);
      } catch (e) { /* next minute */ }
    };
    const interval = setInterval(tick, POLL_MS);
    return () => clearInterval(interval);
  }, [view, page, q, loadThreads]);

  // ---- actions ----------------------------------------------------------

  const refreshAfterAction = useCallback(async (key) => {
    await loadThreads(1, false);
    if (key && key === openKey) {
      try {
        const r = await api.get(`/inbox/threads/${encodeURIComponent(key)}`, { params: { mailbox_id: mailboxId || undefined, mark_read: false } });
        setThreadData(r.data);
      } catch (e) { setThreadData(null); setOpenKey(''); }
    }
  }, [loadThreads, openKey, mailboxId]);

  const threadAction = useCallback(async (action, payload = {}, messageId = null) => {
    if (!openKey) return;
    try {
      if (messageId) {
        await api.post(`/inbox/messages/${messageId}/flag/${action}`, payload);
      } else {
        await api.post(`/inbox/threads/${encodeURIComponent(openKey)}/${action}`, payload, { params: { mailbox_id: mailboxId || undefined } });
      }
      if (action === 'archive' || action === 'unread') {
        setNotice(action === 'archive' ? 'Conversation archived. It is still under Archived — nothing is deleted.' : 'Marked unread.');
        setThreadData(null);
        setOpenKey('');
        setMobilePane('list');
        loadThreads(1, false);
        loadMailboxes();
        setTimeout(() => setNotice(''), 4000);
      } else {
        refreshAfterAction(openKey);
      }
    } catch (e) { setError(errText(e)); }
  }, [openKey, mailboxId, loadThreads, loadMailboxes, refreshAfterAction]);

  const snoozeThread = useCallback(async (choice) => {
    if (!openKey) return;
    try {
      const body = choice.preset ? { preset: choice.preset } : { until: choice.until };
      const r = await api.post(`/inbox/threads/${encodeURIComponent(openKey)}/snooze`, body, { params: { mailbox_id: mailboxId || undefined } });
      setNotice(`Snoozed until ${fmtDateLong(r.data.snoozed_until)} — it comes back with a chime.`);
      setThreadData(null); setOpenKey(''); setMobilePane('list');
      loadThreads(1, false);
      setTimeout(() => setNotice(''), 5000);
    } catch (e) { setError(errText(e)); }
  }, [openKey, mailboxId, loadThreads]);

  const remindMessage = useCallback(async (message, choice) => {
    try {
      const body = choice.preset ? { preset: choice.preset } : { at: choice.until };
      const r = await api.post(`/inbox/messages/${message.id}/remind`, body);
      setNotice(`Reminder set for ${fmtDateLong(r.data.at)}.`);
      setTimeout(() => setNotice(''), 4000);
    } catch (e) { setError(errText(e)); }
  }, []);

  const bulk = useCallback(async (action) => {
    if (!selected.size) return;
    try {
      await api.post('/inbox/threads/bulk', { keys: Array.from(selected), action, mailbox_id: mailboxId || undefined });
      setSelected(new Set());
      loadThreads(1, false);
      loadMailboxes();
    } catch (e) { setError(errText(e)); }
  }, [selected, mailboxId, loadThreads, loadMailboxes]);

  const onQueued = useCallback((item, undoSeconds, label, draftBack) => {
    setComposer(null);
    setInlineComposer(null);
    if (!item) return;
    if (undoSeconds > 0) {
      setUndo({
        id: item.id, expires: Date.now() + undoSeconds * 1000, undoable: true, label: 'Message sent', draftBack,
      });
    } else {
      setUndo({
        id: item.id, expires: Date.now() + 8000, undoable: false,
        label: `Scheduled: ${label || item.send_at_ist || fmtDateLong(item.send_at)}`,
        link: { label: 'View scheduled', onClick: () => { setView('scheduled'); setUndo(null); } },
      });
    }
    if (view === 'sent' || view === 'inbox') setTimeout(() => loadThreads(1, false), Math.min(undoSeconds * 1000 + 3000, 40000));
    if (view === 'scheduled') loadOutbox();
    if (view === 'drafts') loadDrafts();
  }, [view, loadThreads, loadOutbox, loadDrafts]);

  const undoSend = useCallback(async () => {
    if (!undo) return;
    try {
      const r = await api.post(`/inbox/outbox/${undo.id}/undo`);
      const d = r.data?.draft || {};
      const back = undo.draftBack || {};
      setUndo(null);
      const draft = { mailbox_id: d.mailbox_id, to: d.to, cc: d.cc, bcc: d.bcc, subject: d.subject, body_text: d.body_text };
      if (back.mode && back.mode !== 'new' && back.original) {
        setInlineComposer({ mode: back.mode, original: back.original, mailboxId: back.mailboxId, draft });
      } else {
        setComposer({ mode: 'new', mailboxId: d.mailbox_id, draft });
      }
      if ((d.attachment_count || 0) > 0) setNotice('Message pulled back. Attachments need adding again.');
    } catch (e) {
      setUndo(null);
      setError(errText(e));
    }
  }, [undo]);

  const fetchNow = async () => {
    setSyncing(true);
    setError('');
    try {
      const r = await api.post('/inbox/sync', null, { params: mailboxId ? { mailbox_id: mailboxId } : {} });
      const n = r.data?.new || 0;
      setNotice(n ? `${n} new message${n === 1 ? '' : 's'}.` : 'Nothing new.');
      setTimeout(() => setNotice(''), 3000);
      await Promise.all([loadMailboxes(), loadThreads(1, false)]);
    } catch (e) { setError(errText(e)); } finally { setSyncing(false); }
  };

  const openDraft = (d) => {
    const draft = { ...d, to: Array.isArray(d.to) ? d.to.join(', ') : d.to };
    if (d.reply_to_message_id && d.thread_key) {
      openThread(d.thread_key, d.mailbox_id).then(() => setInlineComposer({ mode: d.kind === 'compose' ? 'reply' : d.kind, original: null, mailboxId: d.mailbox_id, draft }));
    } else {
      setComposer({ mode: 'new', mailboxId: d.mailbox_id, draft });
    }
  };

  // ---- keyboard shortcuts ----------------------------------------------

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Escape') {
        if (helpOpen) setHelpOpen(false);
        else if (composer) setComposer(null);
        else if (inlineComposer) setInlineComposer(null);
        return;
      }
      if (isTyping()) return;
      const latest = threadData?.messages?.[threadData.messages.length - 1];
      if (goPrefix.current) {
        goPrefix.current = false;
        if (e.key === 'i') setView('inbox');
        if (e.key === 's') setView('sent');
        return;
      }
      switch (e.key) {
        case 'c': e.preventDefault(); setComposer({ mode: 'new', mailboxId }); break;
        case 'r': if (latest) { e.preventDefault(); setInlineComposer({ mode: 'reply', original: latest, mailboxId: latest.mailbox_id }); } break;
        case 'a': if (latest) { e.preventDefault(); setInlineComposer({ mode: 'replyAll', original: latest, mailboxId: latest.mailbox_id }); } break;
        case 'f': if (latest) { e.preventDefault(); setInlineComposer({ mode: 'forward', original: latest, mailboxId: latest.mailbox_id }); } break;
        case 'e': if (openKey) { e.preventDefault(); threadAction(threadData?.thread?.archived ? 'unarchive' : 'archive'); } break;
        case 's': if (openKey) { e.preventDefault(); threadAction(threadData?.thread?.starred ? 'unstar' : 'star'); } break;
        case 'j': e.preventDefault(); setCursor((i) => Math.min(threads.length - 1, i + 1)); break;
        case 'k': e.preventDefault(); setCursor((i) => Math.max(0, i - 1)); break;
        case 'Enter': if (cursor >= 0 && threads[cursor]) { e.preventDefault(); openThread(threads[cursor].thread_key, threads[cursor].mailbox_id); } break;
        case 'x': if (cursor >= 0 && threads[cursor]) {
          e.preventDefault();
          const key = threads[cursor].thread_key;
          setSelected((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
        } break;
        case 'u': e.preventDefault(); setThreadData(null); setOpenKey(''); setMobilePane('list'); break;
        case '/': e.preventDefault(); searchRef.current?.focus(); break;
        case '?': e.preventDefault(); setHelpOpen((v) => !v); break;
        case 'g': goPrefix.current = true; setTimeout(() => { goPrefix.current = false; }, 1200); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [helpOpen, composer, inlineComposer, threadData, openKey, threads, cursor, mailboxId, threadAction, openThread]);

  useEffect(() => {
    if (cursor >= 0 && threads[cursor] && !mobile) openThread(threads[cursor].thread_key, threads[cursor].mailbox_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  // ---- render -----------------------------------------------------------

  const canSend = mailboxes.some((m) => m.can_send);
  const totalUnread = Object.values(unread).reduce((a, b) => a + b, 0);
  const showList = !mobile || mobilePane === 'list';
  const showThread = !mobile || mobilePane === 'thread';

  const rail = (
    <aside className="w-56 flex-shrink-0 flex flex-col overflow-y-auto" style={{ background: C.card, borderRight: `1px solid ${C.border}` }} data-testid="inbox-rail">
      <div className="p-3">
        <button
          type="button"
          onClick={() => setComposer({ mode: 'new', mailboxId })}
          disabled={!canSend}
          className="w-full flex items-center justify-center gap-2 text-sm px-3 py-2.5 rounded-xl text-white disabled:opacity-40"
          style={{ background: C.navy }}
          title={canSend ? 'Compose (c)' : 'No mailbox can send yet'}
          data-testid="inbox-compose"
        >
          <Plus className="w-4 h-4" /> Compose
        </button>
      </div>
      <nav className="px-2 space-y-0.5">
        {VIEWS.map((v) => {
          const Icon = v.icon;
          const active = view === v.id;
          const count = v.id === 'inbox' ? (mailboxId ? unread[mailboxId] || 0 : totalUnread) : v.id === 'scheduled' ? outbox.filter((o) => o.status === 'scheduled').length : v.id === 'drafts' ? drafts.length : 0;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => { setView(v.id); setLabel(''); setThreadData(null); setOpenKey(''); setMobilePane('list'); }}
              className="w-full flex items-center gap-2 text-sm px-2.5 py-1.5 rounded-lg text-left"
              style={{ background: active ? C.soft : 'transparent', color: C.ink, fontWeight: active ? 600 : 400 }}
              data-testid={`view-${v.id}`}
            >
              <Icon className="w-4 h-4" style={{ color: active ? C.gold : C.muted }} />
              <span className="flex-1 truncate">{v.label}</span>
              {count > 0 && <span className="text-[11px] px-1.5 rounded-full" style={{ background: C.gold, color: '#fff' }}>{count}</span>}
            </button>
          );
        })}
      </nav>
      {labels.length > 0 && (
        <div className="px-2 mt-3">
          <div className="px-2.5 text-[11px] uppercase tracking-wide" style={{ color: C.muted }}>Labels</div>
          {labels.map((l) => (
            <button key={l} type="button" onClick={() => { setView('label'); setLabel(l); setThreadData(null); setOpenKey(''); }} className="w-full flex items-center gap-2 text-sm px-2.5 py-1 rounded-lg text-left" style={{ background: view === 'label' && label === l ? C.soft : 'transparent', color: C.ink }}>
              <Tag className="w-3.5 h-3.5" style={{ color: C.muted }} /> <span className="truncate">{l}</span>
            </button>
          ))}
        </div>
      )}
      <div className="px-2 mt-3">
        <div className="px-2.5 text-[11px] uppercase tracking-wide" style={{ color: C.muted }}>Mailboxes</div>
        <button type="button" onClick={() => setMailboxId('')} className="w-full flex items-center gap-2 text-sm px-2.5 py-1 rounded-lg text-left" style={{ background: !mailboxId ? C.soft : 'transparent', color: C.ink }}>
          <Mail className="w-3.5 h-3.5" style={{ color: C.muted }} /> All mail
          {totalUnread > 0 && <span className="ml-auto text-[11px]" style={{ color: C.muted }}>{totalUnread}</span>}
        </button>
        {mailboxes.map((m) => (
          <button key={m.id} type="button" onClick={() => setMailboxId(m.id)} className="w-full flex items-center gap-2 text-sm px-2.5 py-1 rounded-lg text-left" style={{ background: mailboxId === m.id ? C.soft : 'transparent', color: C.ink }} title={m.last_error ? `Last sync failed: ${m.last_error}` : (m.can_receive ? `Last fetched ${fmtDateLong(m.last_sync_at) || 'never'}` : 'No receiving password yet — Settings › Mailboxes')} data-testid={`mailbox-${m.id}`}>
            <span className="w-2 h-2 rounded-full" style={{ background: m.can_receive ? (m.last_error ? '#d97706' : '#16a34a') : '#d1d5db' }} />
            <span className="truncate">{m.email}</span>
            {!m.can_receive && <span className="text-[11px]" style={{ color: '#b45309' }}>!</span>}
            {unread[m.id] > 0 && <span className="ml-auto text-[11px]" style={{ color: C.muted }}>{unread[m.id]}</span>}
          </button>
        ))}
      </div>
      <div className="mt-auto p-3 space-y-2">
        <ChimeToggle className="w-full justify-center" />
        <button type="button" onClick={() => setHelpOpen(true)} className="w-full inline-flex items-center justify-center gap-1 text-xs px-2 py-1 rounded" style={{ color: C.muted }}>
          <Keyboard className="w-3.5 h-3.5" /> Shortcuts (?)
        </button>
      </div>
    </aside>
  );

  const list = (
    <section className={`flex flex-col min-w-0 ${mobile ? 'flex-1' : 'w-[26rem] flex-shrink-0'}`} style={{ background: C.card, borderRight: `1px solid ${C.border}` }} data-testid="inbox-list">
      <div className="p-2.5 flex items-center gap-2" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5" style={{ color: C.muted }} />
          <input
            ref={searchRef}
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            placeholder="Search mail  (from: to: subject: has:attachment is:unread)"
            className="w-full pl-8 pr-7 py-2 text-sm rounded-lg"
            style={{ border: `1px solid ${C.border}`, fontFamily: FONT }}
            data-testid="inbox-search"
          />
          {queryText && <button type="button" className="absolute right-2 top-2.5" onClick={() => setQueryText('')} aria-label="Clear search"><X className="w-4 h-4" style={{ color: C.muted }} /></button>}
        </div>
        <button type="button" onClick={fetchNow} disabled={syncing} className="p-2 rounded-lg max-md:min-h-[44px] max-md:min-w-[44px] max-md:inline-flex max-md:items-center max-md:justify-center" style={{ border: `1px solid ${C.border}` }} title="Fetch mail now" data-testid="inbox-fetch">
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: C.muted }} /> : <RefreshCw className="w-4 h-4" style={{ color: C.muted }} />}
        </button>
      </div>

      {selected.size > 0 && (
        <div className="px-3 py-1.5 flex items-center gap-2 text-xs" style={{ background: C.soft, borderBottom: `1px solid ${C.border}` }} data-testid="bulk-bar">
          <span style={{ color: C.ink }}>{selected.size} selected</span>
          {[['archive', 'Archive'], ['read', 'Mark read'], ['unread', 'Mark unread'], ['star', 'Star']].map(([a, l]) => (
            <button key={a} type="button" className="underline" style={{ color: C.navy }} onClick={() => bulk(a)}>{l}</button>
          ))}
          <button type="button" className="ml-auto" style={{ color: C.muted }} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {view === 'scheduled' ? (
          <ScheduledList
            items={outbox}
            onCancel={async (it) => { try { await api.delete(`/inbox/outbox/${it.id}`); loadOutbox(); } catch (e) { setError(errText(e)); } }}
            onSendNow={async (it) => { try { await api.post(`/inbox/outbox/${it.id}/send-now`); setNotice('Sending now.'); setTimeout(() => { setNotice(''); loadOutbox(); }, 8000); } catch (e) { setError(errText(e)); } }}
            onReschedule={async (it) => {
              const when = window.prompt('New send time (IST), e.g. 2026-09-08T08:00, or tomorrow_morning / tomorrow_afternoon / monday_morning', it.send_at?.slice(0, 16) || '');
              if (!when) return;
              try { await api.put(`/inbox/outbox/${it.id}`, { send_at: when }); loadOutbox(); } catch (e) { setError(errText(e)); }
            }}
            onOpen={async (it) => {
              try {
                const r = await api.get(`/inbox/outbox/${it.id}`);
                const d = r.data.outbox;
                window.alert(`To: ${(d.to || []).join(', ')}\nCc: ${(d.cc || []).join(', ')}\nSubject: ${d.subject}\nGoes out: ${d.send_at_ist}\n\n${d.body_text}`);
              } catch (e) { setError(errText(e)); }
            }}
          />
        ) : view === 'drafts' ? (
          <DraftsList items={drafts} onOpen={openDraft} onDelete={async (d) => { try { await api.delete(`/inbox/drafts/${d.id}`); loadDrafts(); } catch (e) { setError(errText(e)); } }} />
        ) : (
          <>
            {loadingList && threads.length === 0 && <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" style={{ color: C.muted }} /></div>}
            {!loadingList && threads.length === 0 && (
              <div className="p-8 text-center text-sm" style={{ color: C.muted }}>
                {q ? 'Nothing matches that search.' : mailboxes.length === 0 ? 'No mailbox has been shared with you yet.' : 'Nothing here.'}
              </div>
            )}
            {threads.map((t, i) => {
              const active = t.thread_key === openKey;
              const isSel = selected.has(t.thread_key);
              const names = (t.participants || []).map((p) => (p.name || p.email || '').split(' ')[0]).filter(Boolean);
              const who = names.length ? names.slice(0, 3).join(', ') : (t.latest_from?.email || 'me');
              return (
                <div
                  key={t.thread_key}
                  className="flex items-start gap-2 px-3 py-2.5 cursor-pointer"
                  style={{
                    background: active ? C.soft : (cursor === i ? '#fbf8ef' : (t.unread ? C.card : '#fcfbf8')),
                    borderBottom: `1px solid ${C.border}`, borderLeft: `3px solid ${active ? C.gold : 'transparent'}`,
                  }}
                  onClick={() => { setCursor(i); openThread(t.thread_key, t.mailbox_id); }}
                  data-testid={`thread-row-${t.thread_key}`}
                >
                  <button type="button" className="mt-0.5 max-md:p-[15px] max-md:-m-[15px]" aria-label="Select" onClick={(e) => { e.stopPropagation(); setSelected((prev) => { const n = new Set(prev); if (n.has(t.thread_key)) n.delete(t.thread_key); else n.add(t.thread_key); return n; }); }}>
                    {isSel ? <CheckSquare className="w-4 h-4" style={{ color: C.navy }} /> : <Square className="w-4 h-4" style={{ color: C.border }} />}
                  </button>
                  <button type="button" className="mt-0.5 max-md:p-[15px] max-md:-m-[15px]" aria-label="Star" onClick={async (e) => { e.stopPropagation(); try { await api.post(`/inbox/threads/${encodeURIComponent(t.thread_key)}/${t.starred ? 'unstar' : 'star'}`, {}); loadThreads(page, false); } catch (err) { setError(errText(err)); } }}>
                    <Star className="w-4 h-4" style={{ color: t.starred ? C.gold : C.border, fill: t.starred ? C.gold : 'none' }} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className={`text-sm truncate ${t.unread ? 'font-semibold' : ''}`} style={{ color: C.ink }}>{who}</span>
                      {t.count > 1 && <span className="text-[11px]" style={{ color: C.muted }}>{t.count}</span>}
                      <span className="ml-auto text-[11px] flex-shrink-0" style={{ color: C.muted }}>{fmtDate(t.date)}</span>
                    </div>
                    <div className={`text-sm truncate ${t.unread ? 'font-semibold' : ''}`} style={{ color: C.ink }}>
                      {t.important && <AlertOctagon className="w-3 h-3 inline mr-1" style={{ color: '#b45309' }} />}
                      {t.subject}
                    </div>
                    <div className="text-xs truncate flex items-center gap-1" style={{ color: C.muted }}>
                      {t.has_attachments && <Paperclip className="w-3 h-3 flex-shrink-0" />}
                      {t.snoozed_until && <Clock className="w-3 h-3 flex-shrink-0" />}
                      {(t.labels || []).slice(0, 2).map((l) => <span key={l} className="text-[10px] px-1 rounded" style={{ background: C.soft, border: `1px solid ${C.border}` }}>{l}</span>)}
                      <span className="truncate">{t.latest_from?.direction === 'outbound' ? 'You: ' : ''}{t.snippet}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {hasMore && (
              <button type="button" onClick={() => loadThreads(page + 1, true)} disabled={loadingList} className="w-full text-xs py-3" style={{ color: C.navy }}>
                {loadingList ? 'Loading…' : `Load older conversations (${threads.length} of ${total})`}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );

  const reading = (
    <section className="flex-1 min-w-0 flex flex-col" style={{ background: C.cream }} data-testid="inbox-reading">
      {loadingThread && <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" style={{ color: C.muted }} /></div>}
      {!loadingThread && threadData && (
        <ThreadView
          data={threadData}
          mailboxes={mailboxes}
          onBack={() => { setMobilePane('list'); }}
          onAction={threadAction}
          onSnooze={snoozeThread}
          onRemind={remindMessage}
          onQueued={onQueued}
          composer={inlineComposer}
          setComposer={setInlineComposer}
          notice={notice}
        />
      )}
      {!loadingThread && !threadData && (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8" style={{ color: C.muted }}>
          <Mail className="w-10 h-10 mb-3" style={{ color: C.border }} />
          <p className="text-sm">Pick a conversation. Every message in it — theirs and ours — loads here.</p>
          <p className="text-xs mt-1">Press <kbd className="px-1 rounded" style={{ background: C.soft }}>?</kbd> for shortcuts.</p>
        </div>
      )}
    </section>
  );

  return (
    <div className="flex flex-col max-md:pb-[env(safe-area-inset-bottom)]" style={{ height: 'calc(100vh - 0px)', fontFamily: FONT, background: C.cream }} data-testid="inbox-page-v3">
      <MailNotifyBridge onNotification={() => { loadMailboxes(); if (view === 'inbox') loadThreads(1, false); }} />
      <div className="flex items-center gap-3 px-4 py-2.5" style={{ background: C.card, borderBottom: `1px solid ${C.border}` }}>
        <h1 className="text-base font-semibold" style={{ color: C.ink }}>Mailbox</h1>
        <span className="text-xs" style={{ color: C.muted }}>{mailboxes.length} mailbox{mailboxes.length === 1 ? '' : 'es'} · {totalUnread} unread</span>
        {error && <span className="ml-auto text-xs flex items-center gap-1" style={{ color: '#b42318' }} data-testid="inbox-error"><AlertTriangle className="w-3.5 h-3.5" /> {error}</span>}
        {!error && notice && !threadData && <span className="ml-auto text-xs" style={{ color: '#1e6b2f' }}>{notice}</span>}
        {mobile && (
          <button type="button" className="ml-auto text-xs px-2 py-1 rounded-lg max-md:min-h-[44px] max-md:px-4" style={{ border: `1px solid ${C.border}` }} onClick={() => setMobilePane((p) => (p === 'rail' ? 'list' : 'rail'))}>
            {mobilePane === 'rail' ? 'Close' : 'Menu'}
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0 flex">
        {(!mobile || mobilePane === 'rail') && rail}
        {showList && mobilePane !== 'rail' && list}
        {showThread && mobilePane !== 'rail' && reading}
      </div>

      {composer && (
        <Composer
          mode={composer.mode}
          mailboxes={mailboxes}
          mailboxId={composer.mailboxId}
          original={composer.original}
          draft={composer.draft}
          onClose={() => setComposer(null)}
          onQueued={onQueued}
        />
      )}
      {undo && <UndoToast item={undo} onUndo={undoSend} onDismiss={() => setUndo(null)} />}
      {helpOpen && <HelpOverlay onClose={() => setHelpOpen(false)} />}
    </div>
  );
}
